import { useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import {
    MessageCircle,
    Bug,
    Lightbulb,
    Download,
    Trash2,
    Check,
    Power,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useFeedback } from "../context/FeedbackContext";
import { Button } from "../components/ui/button";
import { KIND_LABEL } from "../lib/feedback";
import { formatRelativeTime } from "../lib/progress";

const KIND_TONES = {
    issue: {
        bg: "bg-danger-soft",
        text: "text-danger",
        border: "border-danger-soft",
        icon: Bug,
    },
    idea: {
        bg: "bg-warning-soft",
        text: "text-warning",
        border: "border-warning-soft",
        icon: Lightbulb,
    },
};

const STATUS_TONES = {
    pending: "bg-mist text-slate-600",
    synced: "bg-brand-soft text-brand",
    resolved: "bg-success-soft text-success",
};

const FILTERS = ["all", "issue", "idea"];

export default function AdminFeedback() {
    const { isAdmin } = useAuth();
    const {
        items,
        updateFeedback,
        removeFeedback,
        clearAllFeedback,
        feedbackMode,
        setFeedbackMode,
    } = useFeedback();
    const [filter, setFilter] = useState("all");

    const filtered = useMemo(
        () =>
            filter === "all" ? items : items.filter((f) => f.kind === filter),
        [items, filter],
    );

    if (!isAdmin) return <Navigate to="/" replace />;

    const exportJson = () => {
        try {
            const blob = new Blob([JSON.stringify(items, null, 2)], {
                type: "application/json",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date()
                .toISOString()
                .slice(0, 19)
                .replace(/[:T]/g, "-");
            a.href = url;
            a.download = `tempo-feedback-${stamp}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Feedback exported");
        } catch {
            toast.error("Could not export feedback");
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-feedback-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1 flex items-center gap-1">
                        <MessageCircle size={11} strokeWidth={3} />
                        Operational validation
                    </div>
                    <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight">
                        Feedback
                    </h1>
                    <p className="text-slate-500 mt-2 max-w-xl">
                        Local-only capture of what teachers report from real daily use.
                        Stays on this device until a future Supabase sync is wired up.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={() => setFeedbackMode(!feedbackMode)}
                        variant="outline"
                        className={`focus-ring rounded-xl h-11 px-4 font-bold border ${
                            feedbackMode
                                ? "bg-success-soft border-success/30 text-success hover:bg-success-soft"
                                : "border-mist hover:bg-mist/50"
                        }`}
                        data-testid="feedback-mode-toggle"
                    >
                        <Power size={15} strokeWidth={2.8} className="mr-2" />
                        {feedbackMode ? "Feedback Mode ON" : "Enable Feedback Mode"}
                    </Button>
                </div>
            </div>

            {/* Filters + actions */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div
                    className="inline-flex p-1 rounded-2xl bg-mist"
                    data-testid="feedback-filter-tabs"
                >
                    {FILTERS.map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            aria-pressed={filter === f}
                            data-testid={`feedback-filter-${f}`}
                            className={`focus-ring px-4 h-10 rounded-xl text-sm font-bold capitalize transition-all ${
                                filter === f
                                    ? "bg-white text-slate-900 shadow-sm"
                                    : "text-slate-500 hover:text-slate-700"
                            }`}
                        >
                            {f === "all"
                                ? "All"
                                : KIND_LABEL[f].replace("Suggest ", "")}
                            <span className="ml-1.5 text-xs font-bold opacity-60 tabular-nums">
                                {f === "all"
                                    ? items.length
                                    : items.filter((x) => x.kind === f).length}
                            </span>
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={exportJson}
                        variant="outline"
                        disabled={items.length === 0}
                        className="focus-ring rounded-xl h-10 px-4 font-bold border-mist hover:bg-mist/50 disabled:opacity-50"
                        data-testid="feedback-export-btn"
                    >
                        <Download size={14} strokeWidth={2.8} className="mr-2" />
                        Export JSON
                    </Button>
                    {items.length > 0 && (
                        <Button
                            onClick={() => {
                                if (
                                    window.confirm(
                                        "Clear ALL feedback? This cannot be undone.",
                                    )
                                ) {
                                    clearAllFeedback();
                                    toast.success("Feedback cleared");
                                }
                            }}
                            variant="outline"
                            className="focus-ring rounded-xl h-10 px-4 font-bold text-danger border-danger-soft hover:bg-danger-soft"
                            data-testid="feedback-clear-all-btn"
                        >
                            <Trash2
                                size={14}
                                strokeWidth={2.8}
                                className="mr-2"
                            />
                            Clear all
                        </Button>
                    )}
                </div>
            </div>

            {filtered.length === 0 ? (
                <div
                    className="bg-white rounded-3xl border border-mist card-soft p-10 text-center"
                    data-testid="feedback-empty-state"
                >
                    <div className="font-display font-bold text-2xl uppercase tracking-tight mb-2">
                        {items.length === 0
                            ? "No feedback yet"
                            : "Nothing matches this filter"}
                    </div>
                    <p className="text-sm text-slate-500 max-w-md mx-auto">
                        {items.length === 0
                            ? "Enable Feedback Mode (above) and walk through a real teaching workflow. Every submission lands here."
                            : "Try the other tab."}
                    </p>
                </div>
            ) : (
                <ul className="space-y-3" data-testid="feedback-list">
                    {filtered.map((f) => {
                        const tone = KIND_TONES[f.kind] || KIND_TONES.idea;
                        const Icon = tone.icon;
                        return (
                            <li
                                key={f.id}
                                className={`bg-white rounded-2xl border ${tone.border} p-5 sm:p-6 card-soft`}
                                data-testid={`feedback-item-${f.id}`}
                            >
                                <div className="flex items-start gap-3 mb-3">
                                    <div
                                        className={`w-9 h-9 rounded-xl ${tone.bg} ${tone.text} flex items-center justify-center shrink-0`}
                                    >
                                        <Icon size={16} strokeWidth={2.6} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span
                                                className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${tone.bg} ${tone.text}`}
                                            >
                                                {KIND_LABEL[f.kind]}
                                            </span>
                                            <span
                                                className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                                                    STATUS_TONES[f.status] ||
                                                    STATUS_TONES.pending
                                                }`}
                                            >
                                                {f.status}
                                            </span>
                                            <span className="text-[11px] text-slate-400">
                                                {formatRelativeTime(f.createdAt)}
                                            </span>
                                        </div>
                                        <div className="font-display font-bold text-lg text-slate-900 mt-1">
                                            {f.title}
                                        </div>
                                    </div>
                                </div>

                                <p className="text-sm text-slate-700 whitespace-pre-wrap pl-12">
                                    {f.body}
                                </p>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 pl-12 text-[11px] text-slate-500">
                                    <Meta label="Page" value={f.page} />
                                    <Meta
                                        label="Teacher"
                                        value={
                                            f.teacherName
                                                ? `${f.teacherName} · ${f.role || ""}`
                                                : "—"
                                        }
                                    />
                                    <Meta
                                        label="Workspace"
                                        value={f.workspaceMode || "all"}
                                    />
                                    <Meta
                                        label="Viewport"
                                        value={
                                            f.viewport
                                                ? `${f.viewport.width}×${f.viewport.height}`
                                                : "—"
                                        }
                                    />
                                </div>

                                <div className="flex items-center justify-end gap-2 mt-4 pl-12">
                                    {f.status !== "resolved" && (
                                        <Button
                                            onClick={() =>
                                                updateFeedback(f.id, {
                                                    status: "resolved",
                                                })
                                            }
                                            variant="outline"
                                            className="rounded-lg h-9 px-3 text-xs font-bold border-success/30 text-success hover:bg-success-soft"
                                            data-testid={`feedback-resolve-${f.id}`}
                                        >
                                            <Check
                                                size={13}
                                                strokeWidth={2.8}
                                                className="mr-1.5"
                                            />
                                            Mark resolved
                                        </Button>
                                    )}
                                    <Button
                                        onClick={() => {
                                            removeFeedback(f.id);
                                            toast.success("Removed");
                                        }}
                                        variant="outline"
                                        className="rounded-lg h-9 px-3 text-xs font-bold text-danger border-danger-soft hover:bg-danger-soft"
                                        data-testid={`feedback-delete-${f.id}`}
                                    >
                                        <Trash2 size={13} strokeWidth={2.8} />
                                    </Button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            <div className="text-center">
                <Link
                    to="/"
                    className="text-xs font-bold text-slate-400 hover:text-slate-600"
                    data-testid="feedback-back-link"
                >
                    ← Back to dashboard
                </Link>
            </div>
        </div>
    );
}

function Meta({ label, value }) {
    return (
        <div>
            <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">
                {label}
            </div>
            <div className="text-xs font-mono text-slate-700 truncate" title={value}>
                {value}
            </div>
        </div>
    );
}
